import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInfluencer } from "../../../Providers/Influencer";
import {
  PCPageBody,
  PostFormatTabsChip,
} from "./PricingCalculatorComponents/PriceCalMolecules/PriceCalMolecules";
import NumberCounter from "../../Brand/DashBoard/PromoteAd/Helpers/NumberCounter";
import { RiSparkling2Line } from "react-icons/ri";
import { PostFormatTypes } from "./PricingCalculatorHelpers/PriceCalHelpers";
import { Button } from "../../../shadcnComponents/ui/button";
import { ArrowRight } from "lucide-react";
import { FaRegShareSquare } from "react-icons/fa";
import { useAuth } from "../../../Providers/Auth";
import { LoginPopup } from "../CreatorLandingPage/CreatorLandingPage";
import mixpanel from "mixpanel-browser";
import { Footer } from "../LocationChapters/Components/organisms";
import { GradientDivider } from "../LocationChapters/Components/atoms";

const PostFormatTabList: PostFormatTypes[] = [
  {
    key: "image",
    value: "Post with Image",
  },
  {
    key: "video",
    value: "Post with Video",
  },
  {
    key: "carousel",
    value: "Post with Carousel",
  },
];

export const ExactPrice = () => {
  const params = useParams();
  const authState = useAuth();

  const influencerState = useInfluencer();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<{
    image: object;
    video: object;
    carousel: object;
  }>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const apiCalled = useRef(false);
  const [postFormat, setPostFormat] = useState<PostFormatTypes["key"]>("image");
  const username = params?.username;
  const [currentData, setCurrentData] = useState(null);
  const [openLoginPopup, setOpenLoginPopup] = useState(false);

  useEffect(() => {
    async function getUserPriceData(postFormat) {
      try {
        if (username) {
          const resData = await influencerState?.getInfluencerExactPrice({
            username: username,
            payload: {
              postFormat: postFormat,
            },
          });
          if (resData?.success) {
            return resData?.data;
          } else if (postFormat === "image") {
            navigate("/linkedin-pricing-calculator");
          }
        }
      } catch (error) {
        console.log(error);
        if (postFormat === "image") {
          navigate("/linkedin-pricing-calculator");
        }
      }
      return null;
    }

    async function fetchData() {
      const formats = ["image", "video", "carousel"];
      setLoading(true);
      formats.forEach(async (format) => {
        const data = await getUserPriceData(format);
        if (data) {
          if (format === "image") {
            setCurrentData(data);
            setLoading(false);
          }

          setUserData((prev) => ({
            ...prev,
            [format]: data,
          }));
        }
      });
    }
    if (params?.username && !apiCalled.current) {
      apiCalled.current = true;
      fetchData();
    }
  }, [influencerState, navigate, params?.username, username]);

  const handleOnChangePostFormat = (key: PostFormatTypes["key"]) => {
    setPostFormat(key);
    setCurrentData(userData[key]);
  };

  const handleShare = async () => {
    mixpanel.track(
      "Pricing Calculator Page : Tell your friends button Clicked"
    );
    if (navigator.share) {
      try {
        const trimmedUrl = window.location.href.replace(/\/u\/.*$/, "");
        const sharingText = `I just checked what I can charge brands with this tool – try it out and see yours too! ${trimmedUrl}`;
        navigator.clipboard.writeText(sharingText);
        await navigator.share({
          text: sharingText,
        });
      } catch (error) {
        console.error("Something went wrong while sharing", error);
      }
    } else {
      console.log("Web Share API is not supported in your browser.");
    }
  };

  const handleJoin = () => {
    mixpanel.track("Pricing Calculator Page : Join button Clicked");
    if (
      authState?.loggedUser &&
      localStorage?.getItem("jwtToken") &&
      localStorage?.getItem("UserType")?.toLowerCase() === "influencer"
    ) {
      navigate("/influencer/dashboard");
    } else {
      setOpenLoginPopup(true);
    }
  };

  return (
    <>
      {openLoginPopup && (
        <LoginPopup onClose={() => setOpenLoginPopup(false)} backToSource />
      )}

      <PCPageBody>
        {loading && <div>Loading...</div>}
        {!loading && userData && (
          <div className="flex flex-col items-center justify-center gap-28">
            <div>
              <div className="flex gap-4 justify-center">
                {PostFormatTabList?.map((item, index) => {
                  return (
                    <PostFormatTabsChip
                      key={index}
                      text={item?.value}
                      onClick={() => handleOnChangePostFormat(item?.key)}
                      active={postFormat === item?.key}
                    />
                  );
                })}
              </div>
              <div className="flex flex-col gap-3 justify-center items-center w-full mt-12">
                <div className="inline-flex flex-col justify-center items-center gap-2.5 rounded border px-8 py-2.5 border-solid border-[#454545] relative">
                  <img
                    src="https://anchors-collab.s3.ap-south-1.amazonaws.com/pricingCalculatorSuccess.gif"
                    alt="success"
                    className="absolute -left-20 -top-10 -rotate-12 h-[250px] w-[115px]"
                  />
                  <img
                    src="https://anchors-collab.s3.ap-south-1.amazonaws.com/pricingCalculatorSuccess.gif"
                    alt="success"
                    className="absolute -right-20 -top-10 rotate-12 h-[250px] w-[115px]"
                  />
                  <span className="text-[#C6C6C6] text-center font-[Inter] text-sm not-italic font-normal leading-[22px]">
                    Per Post Pricing
                  </span>
                  <span
                    style={{
                      backgroundColor:
                        "linear-gradient(89deg, #FFF 1.52%, #FFC3C3 49.65%, #FF8890 97.79%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                    className="w-full flex items-center justify-center text-center text-5xl not-italic font-bold leading-[normal] bg-[linear-gradient(89deg,#FFF_1.52%,#FFC3C3_49.65%,#FF8890_97.79%)] bg-clip-text"
                  >
                    ₹
                    {
                      <NumberCounter
                        className="text-5xl"
                        prevNumber={0}
                        targetNumber={Math.ceil(
                          currentData?.minExpectedSpendAmount
                        )}
                        interval={currentData?.minExpectedSpendAmount / 100}
                      />
                    }{" "}
                    - ₹
                    {
                      <NumberCounter
                        className="text-5xl"
                        prevNumber={0}
                        targetNumber={Math.ceil(
                          currentData?.maxExpectedSpendAmount
                        )}
                        interval={currentData?.maxExpectedSpendAmount / 100}
                      />
                    }
                    {"*"}
                  </span>
                  <RiSparkling2Line
                    size={20}
                    color="#FFDD55"
                    className=" absolute -bottom-2 -right-2 animate-pulse"
                  />
                </div>
                <div className="w-full max-w-screen-sm flex items-center">
                  <span className="text-[#919191] font-[Inter] text-xs not-italic font-normal leading-[18px] break-words">
                    Note:
                    <ol className="list-decimal px-3" start={1}>
                      <li>
                        Insights from thousands of LinkedIn interactions and
                        100+ profile data points against market trends are
                        combined to offer this competitive estimate.
                      </li>
                      <li>
                        Actual charges may vary due to the brand's type, stage,
                        product, target audience & campaign goals.
                      </li>
                    </ol>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-5">
              <Button
                className="gap-3 px-6 text-sm font-medium leading-[24px]"
                variant="secondary"
                onClick={handleShare}
              >
                Tell Your Friends
                <FaRegShareSquare size={16} />
              </Button>
              <Button
                className="bg-[#DC2627] gap-3 px-6 text-sm font-medium leading-[24px]"
                variant="destructive"
                onClick={handleJoin}
              >
                Join Our Creator Network
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </PCPageBody>
      <div className="w-full flex items-center flex-col bg-[#171717] text-white/70">
        <div className="w-full max-w-screen-lg md:px-5">
          <GradientDivider className="h-[1px]" />
          <Footer />
        </div>
      </div>
    </>
  );
};
